import { useEffect, useState } from 'react';
import { ChatAssistantsListActions } from './actions';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
    ChatAssistant,
    ChatAssistantFilter,
    InitialConfiguration,
} from '../../../../shared/types/chat-assistant';
import { TableTooltip } from '../../../components/general/table';
import { useNavigate } from 'react-router-dom';
import { asyncGetChatAssistants } from '../../../../services/chat-assistant.service';
import { ISelect } from '../../../../shared/types/shared';
import { RootState } from '../../../../redux/store';
import { useSelector } from 'react-redux';
import { jsonAssistantDataToString } from '../utils/initialConfiguration';
import { isAdminUser } from '../../../../shared/utils/user';

export const useChatAssistantsList = (filters?: ChatAssistantFilter) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<ChatAssistant[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<ChatAssistantFilter>({});

    const reloadData = () => {
        setLoading(true);
        asyncGetChatAssistants(finalFilters, page, pageSize)
            .then((res) => {
                const items = res.items;
                const formatData = items.map((item) => ({
                    ...item,
                    initialConfiguration: jsonAssistantDataToString(
                        item.initialConfiguration as InitialConfiguration
                    ),
                }));
                setRows(formatData);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    };

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<ChatAssistant>[] = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: isAdminUser(type) ? 150 : 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <ChatAssistantsListActions
                    id={row.id}
                    assistantLinkId={row.row?.assistantLinkId}
                    isAuthUser={!!row?.row?.isAuthorized}
                    reloadData={reloadData}
                />
            ),
        },
        {
            field: 'name',
            headerName: 'Nombre',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => <TableTooltip value={row.row.name || ''} />,
        },
        {
            field: 'description',
            headerName: 'Descripción',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => (
                <TableTooltip value={row.row.description || ''} />
            ),
        },
        {
            field: 'initialConfiguration',
            headerName: 'Configuración inicial',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        JSON.stringify(row.row.initialConfiguration).replaceAll(
                            `"`,
                            ''
                        ) || ''
                    }
                />
            ),
        },
        {
            field: 'project',
            headerName: 'Proyecto',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => (
                <TableTooltip
                    value={(row.row.project as ISelect)?.name || ''}
                />
            ),
        },
    ];

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetChatAssistants(finalFilters, page, pageSize)
            .then((res) => {
                const items = res.items;
                const formatData = items.map((item) => ({
                    ...item,
                    initialConfiguration: jsonAssistantDataToString(
                        item.initialConfiguration as InitialConfiguration
                    ),
                }));
                setRows(formatData);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        navigate,
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
